import {Component, ViewEncapsulation} from '@angular/core';
import {CampaignSettingsAbstractComponent} from '../campaign-settings-abstract.component';
import {Campaign, CampaignSection, LinkMatchSeting, MFAType, VerificationMethod} from '../../_models';
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import {AlertService, CampaignService, LoaderService, SideNavService} from '../../_services';
import {FormBuilder, Validators} from '@angular/forms';
import {NgxSmartModalService} from 'ngx-smart-modal';
import {expandCollapse} from '../../_shared/animations/app-animations';

@Component({
  templateUrl: './campaign-customer-login-settings.component.html',
  styleUrls: ['../campaign-section.scss', './campaign-customer-login-settings.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [expandCollapse],
})
export class CampaignCustomerLoginSettingsComponent extends CampaignSettingsAbstractComponent {
  public section = CampaignSection.LoginSettings;
  public verificationMethod = VerificationMethod;
  public unmatchedVerificationMethods = 'unmatched_verification_methods';
  public matchedVerificationMethods = 'matched_verification_methods';
  duplicateUnmatchedSettings = false;
  requireUnmatchedBilling = false;
  requireMatchedBilling = false;
  public mfaTypes = [
    { value: MFAType.Disabled, label: 'Disabled' },
    { value: MFAType.EmailPreferred, label: 'Email Preferred' },
    { value: MFAType.SMSPreferred, label: 'SMS Preferred' },
    { value: MFAType.BestAvailable, label: 'Best Available' },
    { value: MFAType.UserChoice, label: 'User Choice' },
  ];
  public linkMatchSettings = [
    { value: LinkMatchSeting.Disabled, label: 'Disabled, login required' },
    { value: LinkMatchSeting.Match, label: 'Synchronous match, no login required'},
    { value: LinkMatchSeting.PreMatch, label: 'Asynchronous match, login required and may replace customer in link'},
    { value: LinkMatchSeting.PreMatchStrict,
      label: 'Asynchronous match, login required and must match customer in link'},
  ]

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected campaignService: CampaignService,
    protected alertService: AlertService,
    protected formBuilder: FormBuilder,
    protected sideNav: SideNavService,
    protected modalService: NgxSmartModalService,
    protected loaderService: LoaderService
  ) {
    super(router, location, route, campaignService, sideNav, alertService, loaderService);
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      unmatched_verify_all: [false],
      matched_verify_all: [false],
      match_phone: [true],
      match_email: [true],
      match_order_number: [false],
      unmatched_verification_methods: [[]],
      matched_verification_methods: [[]],
      mfa_type: [MFAType.Disabled],
      verify_attempts: [3, [Validators.required, Validators.min(1), Validators.max(10000)]],
      login_attempts: [3, [Validators.required, Validators.min(1), Validators.max(10000)]],
      customer_lock_time: [0, [Validators.required, Validators.min(0), Validators.max(1440)]],
      link_match: [LinkMatchSeting.PreMatch],
    });

    super.ngOnInit();
  }

  onCampaignFetch(data: Campaign) {
    // Convert billing methods back to regular for display
    const processedData = { ...data };

    if (processedData.unmatched_verification_methods) {
      const { methods, hasBilling } = this.convertFromBillingMethods(processedData.unmatched_verification_methods);
      processedData.unmatched_verification_methods = methods;
      this.requireUnmatchedBilling = hasBilling;
    }

    if (processedData.matched_verification_methods) {
      const { methods, hasBilling } = this.convertFromBillingMethods(processedData.matched_verification_methods);
      processedData.matched_verification_methods = methods;
      this.requireMatchedBilling = hasBilling;
    }

    processedData.customer_lock_time /= 60; // Convert seconds to minutes
    this.form.patchValue(processedData);

    // Check if unmatched and matched verification methods are identical
    if (this.arraysEqual(processedData.unmatched_verification_methods, processedData.matched_verification_methods)) {
      this.duplicateUnmatchedSettings = true;
    }

  }

  toggleVerificationMethod(method: VerificationMethod, isChecked: boolean, arrayName: string): void {
    const formControl = this.form.get(arrayName);
    const currentMethods = formControl && formControl.value ? formControl.value : [];

    if (isChecked) {
      // For name radio buttons, remove other name verification methods first
      if (method === VerificationMethod.FullName || method === VerificationMethod.LastName) {
        const filteredMethods = currentMethods.filter(
          (m: VerificationMethod) => m !== VerificationMethod.FullName && m !== VerificationMethod.LastName
        );
        const updatedMethods = [...filteredMethods, method];
        if (formControl) {
          formControl.setValue(updatedMethods);
        }
      }
      // For address radio buttons, remove other address verification methods first
      else if (
        method === VerificationMethod.Street ||
        method === VerificationMethod.Zip ||
        method === VerificationMethod.Address ||
        method === VerificationMethod.StreetAndZip
      ) {
        const filteredMethods = currentMethods.filter(
          (m: VerificationMethod) =>
            m !== VerificationMethod.Street &&
            m !== VerificationMethod.Zip &&
            m !== VerificationMethod.Address &&
            m !== VerificationMethod.StreetAndZip
        );

        // Special case for StreetAndZip - add both Street and Zip to the array
        if (method === VerificationMethod.StreetAndZip) {
          const updatedMethods = [...filteredMethods, VerificationMethod.Street, VerificationMethod.Zip];
          if (formControl) {
            formControl.setValue(updatedMethods);
          }
        } else {
          const updatedMethods = [...filteredMethods, method];
          if (formControl) {
            formControl.setValue(updatedMethods);
          }
        }
      } else {
        // For other checkboxes, add the method if it's not already in the array
        if (!currentMethods.includes(method)) {
          const updatedMethods = [...currentMethods, method];
          if (formControl) {
            formControl.setValue(updatedMethods);
          }
        }
      }
    } else {
      // Remove the method from the array
      const updatedMethods = currentMethods.filter((m: VerificationMethod) => m !== method);
      if (formControl) {
        formControl.setValue(updatedMethods);
      }
    }
  }

  isVerificationMethodEnabled(method: VerificationMethod, arrayName: string): boolean {
    const formControl = this.form.get(arrayName);
    const currentMethods = formControl && formControl.value ? formControl.value : [];

    // Special case for StreetAndZip - check if both Street and Zip are present
    if (method === VerificationMethod.StreetAndZip) {
      return currentMethods.includes(VerificationMethod.Street) && currentMethods.includes(VerificationMethod.Zip);
    }

    return currentMethods.includes(method);
  }

  isNameVerificationEnabled(arrayName: string): boolean {
    const formControl = this.form.get(arrayName);
    const currentMethods = formControl && formControl.value ? formControl.value : [];
    return currentMethods.includes(VerificationMethod.FullName) || currentMethods.includes(VerificationMethod.LastName);
  }

  isAddressVerificationEnabled(arrayName: string): boolean {
    const formControl = this.form.get(arrayName);
    const currentMethods = formControl && formControl.value ? formControl.value : [];
    return (
      currentMethods.includes(VerificationMethod.Street) ||
      currentMethods.includes(VerificationMethod.Zip) ||
      currentMethods.includes(VerificationMethod.Address) ||
      (currentMethods.includes(VerificationMethod.Street) && currentMethods.includes(VerificationMethod.Zip))
    );
  }

  toggleNameVerification(isChecked: boolean, arrayName: string): void {
    const formControl = this.form.get(arrayName);
    const currentMethods = formControl && formControl.value ? formControl.value : [];

    if (isChecked) {
      // Enable name verification - add LastName as default if no name method exists
      const hasNameMethod =
        currentMethods.includes(VerificationMethod.FullName) || currentMethods.includes(VerificationMethod.LastName);
      if (!hasNameMethod) {
        const updatedMethods = [...currentMethods, VerificationMethod.LastName];
        if (formControl) {
          formControl.setValue(updatedMethods);
        }
      }
    } else {
      // Disable name verification - remove both name methods
      const filteredMethods = currentMethods.filter(
        (m: VerificationMethod) => m !== VerificationMethod.FullName && m !== VerificationMethod.LastName
      );
      if (formControl) {
        formControl.setValue(filteredMethods);
      }
    }
  }

  toggleAddressVerification(isChecked: boolean, arrayName: string): void {
    const formControl = this.form.get(arrayName);
    const currentMethods = formControl && formControl.value ? formControl.value : [];

    if (isChecked) {
      const updatedMethods = [...currentMethods, VerificationMethod.Address];
      if (formControl) {
        formControl.setValue(updatedMethods);
      }
    } else {
      // Disable address verification - remove all address methods
      const filteredMethods = currentMethods.filter(
        (m: VerificationMethod) =>
          m !== VerificationMethod.Street &&
          m !== VerificationMethod.Zip &&
          m !== VerificationMethod.Address &&
          m !== VerificationMethod.StreetAndZip
      );
      if (formControl) {
        formControl.setValue(filteredMethods);
      }
    }
  }

  toggleDuplicateSettings(isChecked: boolean): void {
    this.duplicateUnmatchedSettings = isChecked;

    if (isChecked) {
      // Copy unmatched verification methods to matched
      const unmatchedControl = this.form.get('unmatched_verification_methods');
      const matchedControl = this.form.get('matched_verification_methods');

      if (unmatchedControl && matchedControl) {
        const unmatchedMethods = unmatchedControl.value || [];
        matchedControl.setValue([...unmatchedMethods]);

        this.form.patchValue({
          matched_verification_methods: [...unmatchedMethods],
        });
      }
    } else {
      // Clear matched verification methods
      const matchedControl = this.form.get('matched_verification_methods');
      if (matchedControl) {
        matchedControl.setValue([]);
      }
    }
  }

  toggleMultifactor(isChecked: boolean): void {
    const mfaTypeControl = this.form.get('mfa_type');

    if (isChecked) {
      mfaTypeControl.patchValue(MFAType.SMSPreferred);
    } else {
      mfaTypeControl.patchValue(MFAType.Disabled);
    }
  }

  get isMFAEnabled(): boolean {
    const mfaType = this.form.get('mfa_type').value;
    return Number(mfaType) !== MFAType.Disabled;
  }

  toggleLinkMatch(isChecked: boolean): void {
    const linkMatchControl = this.form.get('link_match');

    if (isChecked) {
      linkMatchControl.patchValue(LinkMatchSeting.PreMatch);
    } else {
      linkMatchControl.patchValue(LinkMatchSeting.Disabled);
    }
  }

  get isLinkMatchEnabled(): boolean {
    const linkMatch = this.form.get('link_match').value;
    return Number(linkMatch) !== LinkMatchSeting.Disabled;
  }

  private arraysEqual(arr1: any[], arr2: any[]): boolean {
    if (!arr1 && !arr2) {
      return true;
    }

    if (!arr1 || !arr2) {
      return false;
    }

    if (arr1.length !== arr2.length) {
      return false;
    }

    const sorted1 = [...arr1].sort();
    const sorted2 = [...arr2].sort();

    return sorted1.every((val, index) => val === sorted2[index]);
  }

  private convertToBillingMethods(methods: VerificationMethod[], requireBilling: boolean): VerificationMethod[] {
    if (!requireBilling) return methods;

    const regularToBillingMap = new Map([
      [VerificationMethod.Street, VerificationMethod.BillingStreet],
      [VerificationMethod.Zip, VerificationMethod.BillingZip],
      [VerificationMethod.Address, VerificationMethod.BillingAddress],
    ]);

    return methods.map((method) => {
      return regularToBillingMap.get(method) || method;
    });
  }

  private convertFromBillingMethods(methods: VerificationMethod[]): {
    methods: VerificationMethod[];
    hasBilling: boolean;
  } {
    const billingToRegularMap = new Map([
      [VerificationMethod.BillingStreet, VerificationMethod.Street],
      [VerificationMethod.BillingZip, VerificationMethod.Zip],
      [VerificationMethod.BillingAddress, VerificationMethod.Address],
    ]);

    let hasBilling = false;
    const convertedMethods = methods.map((method) => {
      const regularMethod = billingToRegularMap.get(method);
      if (regularMethod) {
        hasBilling = true;
        return regularMethod;
      }
      return method;
    });

    return { methods: convertedMethods, hasBilling };
  }

  protected getFormData(): any {
    let formData = super.getFormData();

    // Convert unmatched verification methods to billing if required
    if (this.requireUnmatchedBilling) {
      const unmatchedMethods = formData.unmatched_verification_methods || [];
      formData.unmatched_verification_methods = this.convertToBillingMethods(unmatchedMethods, true);
    } else {
      // If not requiring billing, convert any billing methods back to regular
      const unmatchedMethods = formData.unmatched_verification_methods || [];
      const convertedMethods = this.convertFromBillingMethods(unmatchedMethods);
      formData.unmatched_verification_methods = convertedMethods.methods;
    }

    // Convert matched verification methods to billing if required
    if (this.requireMatchedBilling) {
      const matchedMethods = formData.matched_verification_methods || [];
      formData.matched_verification_methods = this.convertToBillingMethods(matchedMethods, true);
    } else {
      // If not requiring billing, convert any billing methods back to regular
      const matchedMethods = formData.matched_verification_methods || [];
      const convertedMethods = this.convertFromBillingMethods(matchedMethods);
      formData.matched_verification_methods = convertedMethods.methods;
    }

    formData.customer_lock_time *= 60; // convert minutes to seconds

    return formData;
  }
}
